import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '../views/HomeView.vue'

const routes = [
  {
    path: '/',
    name: 'home',
    component: HomeView
  },
  {
    path: '/entertainment',
    name: 'entertainment',
    component: () => import('../views/EntertainmentHub.vue')
  },
  {
    path: '/book-ride',
    name: 'book-ride',
    component: () => import('../views/BookRideView.vue')
  },
  {
    path: '/courses-books',
    name: 'courses-books',
    component: () => import('../views/CoursesBooksView.vue')
  },
  {
    path: '/admin/dashboard',
    name: 'admin-dashboard',
    component: () => import('../views/admin/Dashboard.vue'),
    meta: { requiresAdmin: true }
  },
  {
    path: '/analytics',
    name: 'analytics',
    component: () => import('../views/AnalyticsView.vue'),
    meta: { requiresAdmin: true }
  },
  {
    path: '/community-forum',
    name: 'community-forum',
    component: () => import('../views/CommunityForumView.vue')
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('../views/DashboardView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/developer-space',
    name: 'developer-space',
    component: () => import('../views/DeveloperSpaceView.vue')
  },
  {
    path: '/donate',
    name: 'donate',
    component: () => import('../views/DonateView.vue')
  },
  {
    path: '/roles',
    name: 'roles',
    component: () => import('../views/RolesView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/roles/apply/:roleId',
    name: 'role-application',
    component: () => import('../views/RoleApplicationView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/subscription',
    name: 'subscription',
    component: () => import('../views/SubscriptionView.vue')
  },
  {
    path: '/guide',
    name: 'guide',
    component: () => import('../views/GuideView.vue')
  },
  {
    path: '/jobs',
    name: 'jobs',
    component: () => import('../views/JobsView.vue')
  },
  {
    path: '/maintenance',
    name: 'maintenance',
    component: () => import('../views/MaintenanceView.vue')
  },
  {
    path: '/marketplace',
    name: 'marketplace',
    component: () => import('../views/MarketplaceView.vue')
  },
  {
    path: '/messaging',
    name: 'messaging',
    component: () => import('../views/MessagingView.vue')
  },
  {
    path: '/orders-requests',
    name: 'orders-requests',
    component: () => import('../views/OrdersRequestsView.vue')
  },
  {
    path: '/professionals',
    name: 'professionals',
    component: () => import('../views/ProfessionalsView.vue')
  },
  {
    path: '/auth/signin',
    name: 'signin',
    component: () => import('../views/auth/SignInView.vue')
  },
  {
    path: '/auth/signup',
    name: 'signup',
    component: () => import('../views/auth/SignUpView.vue')
  },
  {
    path: '/auth/forgot-password',
    name: 'forgot-password',
    component: () => import('../views/auth/ForgotPasswordView.vue')
  },
  {
    path: '/auth/reset-password',
    name: 'reset-password',
    component: () => import('../views/auth/ResetPasswordView.vue')
  },
  {
    path: '/verification',
    name: 'verification',
    component: () => import('../views/VerificationView.vue')
  },
  // NEW ROUTES
  {
    path: '/my-posts',
    name: 'my-posts',
    component: () => import('../views/MyPostsView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/reviews',
    name: 'reviews',
    component: () => import('../views/ReviewsView.vue')
  },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

const API_BASE = process.env.VUE_APP_API_URL
  ? process.env.VUE_APP_API_URL.replace(/\/$/, '')
  : ''

let cachedUser = null
let lastFetched = 0
const AUTH_ROUTE_NAMES = new Set(['signin', 'signup', 'forgot-password', 'reset-password', 'verification'])

function shouldSkipAuthFetch(to) {
  if (AUTH_ROUTE_NAMES.has(to.name)) return true
  if (typeof to.path === 'string' && to.path.startsWith('/auth')) return true
  return false
}

function updateBodyAuth(user) {
  const isAuthed = Boolean(user)
  document.body.dataset.auth = isAuthed ? 'true' : 'false'
  document.body.dataset.role = user?.role ? String(user.role).toLowerCase() : 'guest'
  document.body.dataset.username = user?.username || ''
}

async function fetchCurrentUser(force = false) {
  const now = Date.now()
  if (!force && cachedUser && now - lastFetched < 30000) {
    return cachedUser
  }

  try {
    const response = await fetch(`${API_BASE}/me`, {
      credentials: 'include',
    })
    if (response.ok) {
      const data = await response.json()
      cachedUser = data.user || null
      lastFetched = now
      updateBodyAuth(cachedUser)
      return cachedUser
    }
  } catch (error) {
    console.error('Auth check failed', error)
  }

  cachedUser = null
  updateBodyAuth(null)
  return null
}

router.beforeEach(async (to) => {
  const skipAuthFetch = shouldSkipAuthFetch(to)
  const user = skipAuthFetch ? null : await fetchCurrentUser()

  if (to.meta?.requiresAuth && !user) {
    return { path: '/auth/signin', query: { redirect: to.fullPath } }
  }

  if (to.meta?.requiresAdmin) {
    if (!user) {
      return { path: '/auth/signin', query: { redirect: to.fullPath } }
    }
    const role = user.role ? String(user.role).toLowerCase() : ''
    if (role !== 'admin') {
      return { path: '/dashboard' }
    }
  }

  return true
})

export default router
